import {z} from 'zod'
import {GuestLevelStatusSchema, GuestSchemaV3, HostSchemaV3} from '../authentication/services/auth.schema'
import {GoalStatusesE, GoalTypesE} from '../goal/types'
import {TOP_FAN_LEADER_STATUSES} from '@/components/ui/square-progress/SquareProgress'
import {MediaTypeE} from '@/types'

/* ========== ENUMS & SCHEMAS ========== */

// Message Center Tabs
export enum ChannelsTypesE {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    PAID = 'paid'
}

// Types of channel subscription
export enum ChannelSubscriptionTypesE {
    SUBSCRIPTION = 'subscription',
    REFERRAL = 'referral',
    FREE_CONNECTION = 'free_connection'
}
export const ChannelSubscriptionTypesSchema = z.nativeEnum(ChannelSubscriptionTypesE)

// Subscription Statuses
export enum SubscriptionStatusesE {
    ACTIVE = 'active',
    CANCELED = 'canceled',
    REACTIVABLE = 'reactivable',
    PAUSED = 'paused',
    FAILED = 'failed',
    FREE = 'free',
    INACTIVE = 'inactive'
}
export const SubscriptionStatusesSchema = z.nativeEnum(SubscriptionStatusesE)

// Initiated By Role
export enum InitiatedByRoleE {
    HOST = 'host',
    GUEST = 'guest'
}
export const InitiatedByRoleSchema = z.nativeEnum(InitiatedByRoleE)

// Message Status
export enum MessageStatusE {
    FREE = 'free',
    PAID = 'paid'
}
export const MessageStatusSchema = z.nativeEnum(MessageStatusE)

// Message Type
// Need to align with BE
export enum MessageTypeE {
    MICROMORGI_TRANSACTION = 'micromorgi_transaction',
    GIFT_TRANSACTION = 'gift_transaction',
    IMAGE = 'image',
    VIDEO = 'video',
    PHOTO = 'photo',
    AUDIO = 'audio',
    PAID_MESSAGE = 'paid_text',
    ALREADY_PAID_MESSAGE = 'already_paid_message',
    GOAL = 'goal',
    TRANSACTION_GOAL = 'micromorgi_transaction_goal',
    PAID_MEDIA_TRANSACTION = 'paid_media_transaction',
    SYSTEM_BROADCAST = 'system_broadcast',
    GOAL_TEASER = 'goal_teaser',
    GIF = 'gif',
    TEXT = 'text',
    PAID_MEDIA_TEXT = 'paid_media_text',
    MEGA_GIFT_TRANSACTION = 'mega_gift',
    DELETED = 'deleted',
    PAID_CONNECTION_MESSAGE = 'paid_connection_message',
    PAUSED_SUBSCRIPTION = 'subscription_paused',
    DOCUMENT = 'document',
    FEATURED_HOST = 'featured_rookie',
    TEASER = 'teaser'
}
export const MessageTypeSchema = z.nativeEnum(MessageTypeE)

export enum ChannelStatusE {
    OPEN = 'open',
    CLOSED = 'closed',
    PAUSED = 'paused',
    RESUMED = 'resumed'
}
export const ChannelStatusSchema = z.nativeEnum(ChannelStatusE)

export enum SubscriptionTypeE {
    SUBSCRIPTION = 'subscription',
    REFERRAL = 'referral',
    FREE_CONNECTION = 'free_connection'
}
export const SubscriptionTypeSchema = z.nativeEnum(SubscriptionTypeE)

/* ========== Chat Attachment ========== */

export const chatAttachmentSchema = z.object({
    id: z.number().int(),
    senderId: z.number().int(),
    receiverId: z.number().int(),
    type: z.string(),
    morgi: z.number().int().optional(),
    representativeId: z.number().int().optional(),
    representativeFirstName: z.string().optional(),
    agentId: z.number().int().optional(),
    agentFirstName: z.string().optional(),
    documentId: z.number().int().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    url: z.string().url(),
    blurHash: z.string().optional(),
    blurredUrl: z.string().url().optional(), // Legacy field. We keep it for backward compatibility
    isPaid: z.boolean().optional(),
    groupKeyName: z.string().optional(),
    document: z.object({}).catchall(z.any()).optional(),
    sender: z.any().nullable().optional(),
    receiver: z.any().nullable().optional(),
    isAdultContent: z.boolean()
})

export type ChatAttachment = z.infer<typeof chatAttachmentSchema>

/* ========== Paid Message ========== */

export const paidMessageSchema = z.object({
    id: z.number().int(),
    rookieId: z.number().int().optional(),
    morgi: z.number().int().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    message: z.string().optional(),
    isPaid: z.boolean().optional()
})

/* ========== Message Metadata ========== */

export const MessageMetadataSchema = z.object({
    animationId: z.number().int().optional(),
    goalId: z.number().int().optional(),
    gifUrl: z.string().url().optional(),
    subscription_amount: z.number().optional(),
    featured_rookie_id: z.number().int().optional(),
    message_icon: z.string().optional(),
    representativeFirstName: z.string().optional(),
    featuredHostId: z.number().optional(),
    transaction: z
        .object({
            createdAt: z.string(),
            discountedAmount: z.number(),
            id: z.number(),
            leaderId: z.number(),
            micromorgi: z.number(),
            morgi: z.number().nullable(),
            rookieId: z.number()
        })
        .optional(),
    chatAttachment: chatAttachmentSchema,
    paidMessage: paidMessageSchema
})

/* ========== MESSAGE ========== */

export const MessageSchema = z.object({
    id: z.number().int(),
    channelId: z.number().int(),
    text: z.string().nullable().optional(),
    message: z.string().nullable().optional(),
    type: MessageTypeSchema,
    senderId: z.number().int(),
    receiverId: z.number().int().optional(),
    representativeId: z.number().int().nullable().optional(),
    isStarred: z.boolean().optional(),
    paidMessageId: z.number().int().nullable().optional(),
    chatAttachmentId: z.number().int().nullable().optional(),
    language: z.string().optional(),
    sentAt: z.string().datetime(),
    status: MessageStatusSchema.nullable().optional(),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
    metadata: MessageMetadataSchema,
    gifUrl: z.string().url().optional(),
    featuredRookieId: z.number().optional(),
    goalId: z.number().optional(),
    messageIcon: z.string().optional(),
    attachment: z
        .object({
            type: MessageTypeSchema
        })
        .nullable()
        .optional(),
    animationId: z.number().optional(),
    amount: z.number().optional().nullable()
})
export type Message = z.infer<typeof MessageSchema>

export type ChatMessageList = Message[]

/* ========== SUBSCRIPTION ========== */

export const SubscriptionSchema = z
    .object({
        id: z.number().int(),
        validUntilAt: z.string().datetime().nullable().optional(),
        status: SubscriptionStatusesSchema,
        amount: z.number(),
        discountedAmount: z.number().nullable().optional()
    })
    .partial()
    .optional()

/* ========== TOP FANS INFO ========== */

export const TopFansInfosSchema = z
    .object({
        minAmountToReach: z.number(),
        completedCounter: z.number(),
        pendingCounter: z.number(),
        groupId: z.number().int().optional().nullable()
    })
    .partial()
    .optional()
export type TopFansInfos = z.infer<typeof TopFansInfosSchema>

/* ========== REPRESENTATIVE DATA ========== */

export const RepresentativeDataSchema = z
    .object({
        chatId: z.number().int(),
        isRepresentativeOnChat: z.boolean()
    })
    .partial()
    .optional()

/* ========== CHANNEL METADATA ========== */

export const ChannelMetadataSchema = z.object({
    isPayer: z.boolean(),
    remainingDailyMessagesCount: z.number().int().nullable().optional(),
    remainingDailyOpenChatsCount: z.number().int().nullable().optional(),
    subscription: SubscriptionSchema.optional().nullable(),
    hasGiftedChatMicromorgi: z.boolean().optional(),
    unreadMessagesCount: z.number().int().optional().nullable(),
    represenativeData: RepresentativeDataSchema.optional(),
    topFansInfos: TopFansInfosSchema.optional(),
    topFans: z
        .object({
            position: z.number().optional(),
            amountToReach: z.number(),
            micromorgiAmount: z.number(),
            status: z.nativeEnum(TOP_FAN_LEADER_STATUSES)
        })
        .optional(),

    nextStarredMessageAt: z.date().optional()
})

/* ========== Channel ========== */

export const ChannelSchema = z.object({
    id: z.number().int(),
    type: ChannelSubscriptionTypesSchema,
    rookieId: z.number().int(),
    leaderId: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime().nullable().optional(),
    leaderUnreadMessagesCount: z.number().int(),
    rookieUnreadMessagesCount: z.number().int(),
    isPaymentRequiredForConnection: z.boolean(),
    minMorgiToOpenConnection: z.number().nullable().optional(),
    isPaused: z.boolean(),
    isPaid: z.boolean().nullable().optional(),
    lastActivityAt: z.string().datetime().nullable().optional(),
    subscriptionId: z.number().int().nullable().optional(),
    metadata: ChannelMetadataSchema,
    channelStatus: ChannelStatusSchema,
    initiatedBy: z.number().int(),
    lastMessage: MessageSchema,
    user: z.union([GuestSchemaV3, HostSchemaV3]),
    pk: z.string().optional(),
    levelStatus: GuestLevelStatusSchema, // need to be sure, aline with BE
    isOnline: z.boolean()
})

export type Channel = z.infer<typeof ChannelSchema>

export const createListResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) => {
    return z.object({
        data: z.array(itemSchema),
        meta: z.object({
            total: z.number(),
            perPage: z.number(),
            currentPage: z.number(),
            lastPage: z.number(),
            firstPage: z.number(),
            firstPageUrl: z.string(),
            lastPageUrl: z.string(),
            nextPageUrl: z.string().nullable(),
            previousPageUrl: z.string().nullable()
        })
    })
}

export const ListChannelsResponseSchema = createListResponseSchema(ChannelSchema)

export type ListChannelsResponse = z.infer<typeof ListChannelsResponseSchema>

/* ========== MESSAGES TO SEND ========== */

export const SendMessageRequestMetadataSchema = z
    .object({
        chatAttachmentId: z.number().int().optional(),
        isStarred: z.boolean().optional(),
        morgiAmount: z.number().int().min(1).max(999999).optional(),
        gifUrl: z.string().optional(),
        goalId: z.number().optional(),
        featuredRookieId: z.number().optional(),
        animationId: z.number().optional()
    })
    .partial()
    .optional()

export const MessageToSendSchema = z.object({
    language: z.string().optional(),
    metadata: SendMessageRequestMetadataSchema,
    text: z.string().optional(),
    type: MessageTypeSchema,
    senderId: z.number().int(),
    receiverId: z.number().int()
})

export const SendMessageRequestSchema = z.object({
    message: MessageToSendSchema,
    representedCreatorId: z.number().optional(),
    fileUploadId: z.number().optional()
})

export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>

/* ========== REPORT CATEGORIES ========== */

export const ChatReportCategoriesSchema = z.object({
    id: z.number(),
    keyName: z.string(),
    name: z.string(),
    createdAt: z.string(),
    updatedAt: z.string()
})

export const ChatReportCategoriesResponseSchema = z.array(ChatReportCategoriesSchema)

/* ========== BROADCAST ========== */
export const AttachmentFileSchema = z.object({
    fileUploadId: z.number().optional().describe('Provide to store new medias'),
    goalMediaId: z.number().optional().describe('Provide to remove existing medias')
})
export const CreateBroadcastPayloadSchema = z.object({
    messageType: z.enum(['media', 'teaser', 'text']),
    usersIds: z.array(z.number()),
    goalsIds: z.array(z.number()),
    attachment: AttachmentFileSchema.optional(),
    message: MessageToSendSchema.omit({receiverId: true, senderId: true}).optional(),
    teaserGoalId: z.number().optional()
})

export type CreateBroadcastPayload = z.infer<typeof CreateBroadcastPayloadSchema>

const SendBroadcastMessageSchema = CreateBroadcastPayloadSchema.omit({usersIds: true, goalsIds: true})
const CreateGroupPayloadSchema = CreateBroadcastPayloadSchema.omit({usersIds: true, goalsIds: true})
export type SendBroadcastMessageSchema = z.infer<typeof SendBroadcastMessageSchema>
export type CreateGroupPayload = z.infer<typeof CreateGroupPayloadSchema>

export const BroadcastSchema = z.object({
    id: z.number(),
    senderId: z.number(),
    isGoal: z.boolean().nullable(),
    displayName: z.string().nullable(),
    representativeId: z.number().nullable(),
    imageLink: z.string().nullable(),
    createdAt: z.string(),
    lastMessage: MessageSchema.optional()
})

export type Broadcast = z.infer<typeof BroadcastSchema>

/* ========== GROUP CONVERSATION ========== */

export const GoalMediaSchema = z.object({
    id: z.number().int(),
    goalId: z.number().int(),
    type: z.nativeEnum(MediaTypeE),
    pathLocation: z.string(),
    url: z.string().url(),
    optimizedUrls: z.record(z.string(), z.string()).optional().nullable(),
    optimizedUrl: z.string().url().optional().nullable()
})
export type GoalMedia = z.infer<typeof GoalMediaSchema>

export const GoalProofSchema = z.object({
    id: z.number().int(),
    goalId: z.number().int(),
    type: z.nativeEnum(MediaTypeE),
    pathLocation: z.string(),
    status: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    declinedReason: z.string().optional(),
    adminId: z.number().int().optional(),
    url: z.string().url(),
    optimizedUrls: z.record(z.string(), z.string()).optional().nullable(),
    optimizedUrl: z.string().url().optional().nullable()
})
export type GoalProof = z.infer<typeof GoalProofSchema>

export const GoalTypeDetailsSchema = z.object({
    id: z.number(),
    type: z.nativeEnum(GoalTypesE),
    min: z.number(),
    max: z.number(),
    currencyType: z.string(),
    durationType: z.string(),
    durationValue: z.number(),
    isActive: z.number()
})
export type GoalTypeDetails = z.infer<typeof GoalTypeDetailsSchema>

export const RookieAvatarSchema = z.object({
    id: z.number().int(),
    userId: z.number().int(),
    pathLocation: z.string(),
    main: z.boolean(),
    isFaceRecognitionProcessed: z.boolean(),
    title: z.string(),
    content: z.string(),
    tags: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    url: z.string().url(),
    underValidation: z.boolean()
})
export type RookieAvatar = z.infer<typeof RookieAvatarSchema>

export const RookieGenderSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    keyName: z.string()
})
export type RookieGender = z.infer<typeof RookieGenderSchema>

export const GoalRookieSchema = z.object({
    id: z.number().int(),
    avatar: RookieAvatarSchema.nullable(),
    username: z.string(),
    fullName: z.string(),
    gender: RookieGenderSchema.optional()
})
export type GoalRookie = z.infer<typeof GoalRookieSchema>

export const GoalGroupSchema = z.object({
    id: z.number().int(),
    userId: z.number().int(),
    keyName: z.string(),
    goalId: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
})
export type GoalGroup = z.infer<typeof GoalGroupSchema>

export const GoalBroadcastSchema = z.object({
    id: z.number().int(),
    senderId: z.number().int(),
    representativeId: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    isGoal: z.boolean(),
    displayName: z.string(),
    pivot: z.object({
        goalId: z.number().int(),
        broadcastId: z.number().int()
    })
})
export type GoalBroadcast = z.infer<typeof GoalBroadcastSchema>

export const GoalV3Schema = z.object({
    id: z.number().int(),
    name: z.string(),
    slug: z.string(),
    details: z.string(),
    targetAmount: z.number(),
    currencyType: z.string(),
    rookieId: z.number().int(),
    startDate: z.date(),
    endDate: z.date(),
    thankYouMessage: z.string(),
    cancelledAt: z.string().datetime().nullable().optional(),
    cancelledReason: z.string().optional(),
    typeId: z.number().int(),
    status: z.nativeEnum(GoalStatusesE),
    proofNote: z.string().optional(),
    createdAt: z.string().datetime(),
    hasImageProof: z.boolean(),
    hasVideoProof: z.boolean(),
    broadcast: GoalBroadcastSchema,
    isForOthers: z.boolean(),
    radiobox: z.string(),
    leaderDonationsAmount: z.number().int(),
    isSaved: z.boolean(),
    media: z.array(GoalMediaSchema),
    proofs: z.array(GoalProofSchema),
    type: GoalTypeDetailsSchema,
    donationsCount: z.number(),
    rookie: GoalRookieSchema,
    goalGroup: GoalGroupSchema,
    goalId: z.number().optional()
})
export type GoalV3 = z.infer<typeof GoalV3Schema>

/* ========== Main Group Schema ========== */

export enum GroupStatusE {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    DELETED = 'deleted'
}

export const GroupStatusSchema = z.nativeEnum(GroupStatusE)

export const GroupSchema = z.object({
    id: z.number().int(),
    userId: z.number().int(),
    keyName: z.string(),
    name: z.string(),
    status: GroupStatusSchema, // Enum
    description: z.string(),
    imagePathLocation: z.string(),
    subscriptionPackageAmount: z.number().int(),
    activedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    type: z.string(), // Should be Enum
    usersCount: z.number().int(),
    goal: GoalV3Schema,
    goals: z.array(GoalV3Schema).optional(),
    lastMessage: MessageSchema,
    url: z.string().url()
})
export type Group = z.infer<typeof GroupSchema>

export const GetGroupsResponseSchema = z.object({
    data: z.array(GroupSchema),
    meta: z.object({
        total: z.number(),
        limit: z.number(),
        page: z.number(),
        totalPages: z.number()
    })
})
export type GetGroupsResponse = z.infer<typeof GetGroupsResponseSchema>

/* ========== BROADCAST_MESSAGE ========== */

export const BroadcastMessageSchema = z.object({
    id: z.number(),
    message: z.string().nullable(),
    broadcastId: z.number(),
    createdAt: z.string(),
    representativeFirstName: z.string().nullable(),
    agentId: z.number().nullable(),
    agentFirstName: z.string().nullable(),
    paidMessageId: z.number().int().nullable(),
    paidMessage: paidMessageSchema.omit({isPaid: true}),
    attachment: chatAttachmentSchema.optional(),
    teaser: GoalV3Schema
})
export const ListMessagesResponseSchema = z.array(BroadcastMessageSchema)
export type BroadcastMessage = z.infer<typeof BroadcastMessageSchema>
export type ListMessagesResponse = z.infer<typeof ListMessagesResponseSchema>
